import { ComponentProps, createContext, useContext, useMemo } from 'react'
import AvatarPet from '~components/ui/avatar/avatar-pet'
import type { AvatarSize } from '~components/ui/avatar/types'
import Icon from '~components/ui/icon'
import Pet from '~models/pet'
import { cn } from '~utils/cn'
import convertPetAge from '~utils/pet/convert-pet-age'
import { convertPetGender } from '~utils/pet/convert-pet-gender'

type PetCardProps = {
  pet: Pet.Level1 | undefined
  size?: 'sm' | 'lg'
}

const PetCardContext = createContext<PetCardProps>({ pet: undefined })

const usePetCardContext = () => {
  const context = useContext(PetCardContext)

  if (!context) {
    throw new Error('usePetCardContext must be used within a PetCardContextProvider')
  }

  return context
}

/**
 * @see https://github.com/drtail/drtail-owner-web/pull/691
 * - 글자수가 초과하는 경우에 대비해서 min-w-0을 신경써서 삽입해줘야 합니다. (파일 하단 usage 참조)
 * - 클릭이 가능한 PetCard라면 PetCard 최상위 컴포넌트에 cursor-pointer를 삽입해줘야 합니다.
 * - PetCard가 select 됐다는 것을 표현하기 위해 컴포넌트 사용시에 매번 css를 위한 비즈니스 로직을 작성해야 합니다. (파일 하단 usage 참조)
 */
const PetCard = ({
  children,
  className,
  pet,
  size = 'lg',
  ...props
}: Readonly<ComponentProps<'div'> & PetCardProps>) => {
  const value = useMemo(
    () => ({
      pet,
      size,
    }),
    [pet, size]
  )

  return (
    <PetCardContext.Provider value={value}>
      <div
        {...props}
        className={cn(
          'w-full shrink-0 rounded-2xl px-4 py-3',
          pet && Pet.isIncomplete(pet) && 'opacity-50',
          className
        )}
      >
        {children}
      </div>
    </PetCardContext.Provider>
  )
}

const PetCardAvatar = ({ className }: { className?: string }) => {
  const { pet, size } = usePetCardContext()
  const _size: AvatarSize = size === 'lg' ? '48' : '40'

  return <AvatarPet pet={pet} size={_size} className={className} />
}

const PetCardName = ({ className, ...props }: Readonly<ComponentProps<'div'>>) => {
  const { pet, size } = usePetCardContext()

  return (
    <div
      {...props}
      className={cn(
        'truncate text-gray-900 dark:text-white',
        {
          'text-title-3': size === 'lg',
          'text-body-2': size === 'sm',
        },
        className
      )}
    >
      {pet?.name}
    </div>
  )
}

const PetCardKind = ({ className, ...props }: Readonly<ComponentProps<'div'>>) => {
  const { pet } = usePetCardContext()

  return (
    <div
      {...props}
      className={cn('mt-1 break-words text-gray-500 text-body-2 dark:text-gray-400', className)}
    >
      {pet?.kind?.depth1 && pet?.kind?.depth2
        ? `${pet?.kind?.depth1}, ${pet?.kind?.depth2}`
        : 'Unknown'}
    </div>
  )
}

const PetCardAgeWithGender = ({ className, ...props }: ComponentProps<'div'>) => {
  const { pet } = usePetCardContext()

  return (
    <div
      {...props}
      className={cn(
        'flex min-w-0 items-center text-gray-500 text-body-2 dark:text-gray-400',
        className
      )}
    >
      <div className="flex min-w-0 items-center truncate">
        {pet?.born_at ? <>{convertPetAge(pet.born_at)}</> : null}
        {pet?.born_at ? ',' : ''} {convertPetGender(pet?.gender)}
      </div>
    </div>
  )
}

const PetCardDraft = ({ className, ...props }: Readonly<ComponentProps<'div'>>) => {
  const { pet } = usePetCardContext()

  return pet ? (
    <div
      {...props}
      className={cn(
        'flex flex-gap-x-1 items-center mt-1 break-words text-primary-600 text-title-3 dark:text-extended-error',
        className
      )}
    >
      <p>Incomplete pet information</p>
      <Icon name="draft" className="size-3 fill-primary-600 dark:fill-extended-error" />
    </div>
  ) : null
}

PetCard.Avatar = PetCardAvatar
PetCard.Name = PetCardName
PetCard.Kind = PetCardKind
PetCard.AgeWithGender = PetCardAgeWithGender
PetCard.Draft = PetCardDraft

/**
 * @usage
 *  <PetCard
      pet={pet}
      size="sm"
      className={cn("flex w-fit items-center gap-3 border border-solid border-primary-600", isSelected && 'bg-white')}
    >
      <PetCard.Avatar />
      <div className="min-w-0">
        <PetCard.Name />
        <PetCard.Kind />
        <PetCard.AgeWithGender />
      </div>
      <Icon name="checkmark" className={cn("size-5 fill-gray-500", isSelected && fill-primary-600 )} />
    </PetCard>
 */

export default PetCard
